import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwh203Api, FH130, FH131, FH132 } from '../../../services/pwh203Api';

const EMPTY_MASTER: FH130 = {
  fh130_01: '',
  fh130_11: null,
  fh130_12: 0,
  fh130_13: 0,
  fh130_14: '',
  fh130_15: '',
  fh130_16: 'NTD',
  fh130_17: 1,
  fh130_18: 0,
  fh130_19: '',
  fh130_20: 0,
  fh130_21: null,
  fh130_80: 'N',
  fh130_81: '',
  fh130_90: 1,
  fh130_91: '',
  fh130_92: null,
  fh130_93: '',
};

const EMPTY_DETAIL: Omit<FH131, 'fh131_01' | 'fh131_02'> = {
  fh131_11: '',
  fh131_12: '',
  fh131_13: '',
  fh131_14: '',
  fh131_15: null,
  fh131_16: '',
  fh131_17: 0,
  fh131_18: 1,
  fh131_19: '',
  fh131_20: '',
  fh131_21: '',
  fh131_22: 0,
  fh131_23: '',
  fh131_24: null,
  fh131_25: 0,
  fh131_261: false,
  fh131_262: false,
  fh131_27: false,
  fh131_90: 1,
};

export function usePWH203Record() {
  const [record, setRecord] = useState<FH130 | null>(null);
  const [formData, setFormData] = useState<FH130>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<FH131[]>([]);
  const [expenses, setExpenses] = useState<FH132[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pwh203Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details ?? []);
      setExpenses(
        (data.details ?? []).flatMap((d) => d.expenses ?? [])
      );
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ ...EMPTY_MASTER });
    setDetails([]);
    setExpenses([]);
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ?? []);
      setExpenses((record.details ?? []).flatMap((d) => d.expenses ?? []));
    }
  }, [record]);

  const getField = useCallback(
    <K extends keyof FH130>(key: K): FH130[K] => formData[key],
    [formData]
  );

  const setField = useCallback(
    <K extends keyof FH130>(key: K, value: FH130[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSave = useCallback(
    async (setBrowse: () => void) => {
      try {
        const payload = { master: formData, details, expenses };
        let saved: FH130;
        if (record) {
          saved = await pwh203Api.update(record.fh130_01, payload);
        } else {
          saved = await pwh203Api.create(payload);
        }
        setRecord(saved);
        setFormData({ ...saved });
        setDetails(saved.details ?? []);
        setExpenses((saved.details ?? []).flatMap((d) => d.expenses ?? []));
        message.success('存檔成功');
        setBrowse();
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { errors?: { message: string }[] } } };
        const errs = axiosErr?.response?.data?.errors;
        if (errs && errs.length > 0) {
          message.error(errs.map((e) => e.message).join('\n'));
        } else {
          message.error('存檔失敗');
        }
      }
    },
    [formData, details, expenses, record]
  );

  const handleDelete = useCallback(
    async (setBrowse: () => void) => {
      if (!record) return;
      try {
        await pwh203Api.delete(record.fh130_01);
        setRecord(null);
        setFormData({ ...EMPTY_MASTER });
        setDetails([]);
        setExpenses([]);
        message.success('刪除成功');
        setBrowse();
      } catch {
        message.error('刪除失敗');
      }
    },
    [record]
  );

  const addDetailRow = useCallback(() => {
    setDetails((prev) => [
      ...prev,
      {
        ...EMPTY_DETAIL,
        fh131_01: formData.fh130_01,
        fh131_02: prev.length + 1,
        fh131_16: formData.fh130_16,
      } as FH131,
    ]);
  }, [formData.fh130_01, formData.fh130_16]);

  const deleteDetailRow = useCallback((index: number) => {
    setDetails((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((d, i) => ({ ...d, fh131_02: i + 1 }))
    );
  }, []);

  const updateDetailRow = useCallback(
    <K extends keyof FH131>(index: number, key: K, value: FH131[K]) => {
      setDetails((prev) =>
        prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
      );
    },
    []
  );

  return {
    record,
    formData,
    details,
    expenses,
    loading,
    loadRecord,
    resetForAdd,
    revertToRecord,
    getField,
    setField,
    handleSave,
    handleDelete,
    addDetailRow,
    deleteDetailRow,
    updateDetailRow,
  };
}
